import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Briefcase, Search, Sparkles, ShieldAlert, AlertTriangle, FileText,
  Loader2, Users, Receipt, Calendar, MapPin, TrendingUp, UserX, FlaskConical
} from "lucide-react";
import { toast } from "sonner";
import NotFoundCard from "../components/NotFoundCard";
import COIUploader from "../components/COIUploader";
import MCA21SourceLookup from "../components/MCA21SourceLookup";

const API = `${window.location.origin}/api/mca21`;

const FLAG_COLORS = {
  critical: { bg: "rgba(220, 38, 38, 0.18)", border: "#dc2626", text: "#fca5a5" },
  high:     { bg: "rgba(239, 68, 68, 0.12)", border: "#ef4444", text: "#ef4444" },
  medium:   { bg: "rgba(245, 158, 11, 0.10)", border: "#f59e0b", text: "#f59e0b" },
  low:      { bg: "rgba(34, 197, 94, 0.10)",  border: "#22c55e", text: "#22c55e" },
};

const STATUS_COLORS = {
  Active: "#22c55e",
  "Strike Off": "#ef4444",
  "Under Liquidation": "#dc2626",
  Dormant: "#f59e0b",
  Amalgamated: "#94a3b8",
  Converted: "#94a3b8",
};

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>{label}</div>
        <Icon style={{ width: 16, height: 16, color }} />
      </div>
      <div style={{ color, fontSize: "28px", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function formatINR(n) {
  if (!n) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function DataPill({ icon: Icon, label, value, mono, valueColor }) {
  return (
    <div style={{ background: "#080c12", borderRadius: "6px", padding: "10px 12px", display: "flex", alignItems: "center", gap: "10px" }}>
      <Icon style={{ width: 14, height: 14, color: "#64748b" }} />
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ color: "#64748b", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
        <div style={{ color: valueColor || "#f1f5f9", fontSize: "13px", fontWeight: 600, fontFamily: mono ? "ui-monospace, monospace" : "inherit", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value || "—"}</div>
      </div>
    </div>
  );
}

function CINPart({ label, value, valueColor }) {
  return (
    <div>
      <div style={{ color: "#64748b", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ color: valueColor || "#f1f5f9", fontSize: 12, fontWeight: 600, fontFamily: "ui-monospace, monospace" }}>{value || "—"}</div>
    </div>
  );
}

function DirectorCard({ d }) {
  const isRisk = d.is_disqualified || d.is_nominee;
  return (
    <div style={{ background: "#080c12", border: isRisk ? "1px solid #ef4444" : "1px solid #1e2530", borderRadius: "8px", padding: "10px 12px" }}>
      <div className="flex items-center justify-between mb-1">
        <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{d.name}</div>
        <div className="flex gap-1">
          {d.is_disqualified && <span style={{ background: "rgba(220,38,38,0.18)", color: "#fca5a5", fontSize: "9px", padding: "2px 6px", borderRadius: "3px", fontWeight: 700 }}>DISQ</span>}
          {d.is_nominee && <span style={{ background: "rgba(245,158,11,0.18)", color: "#f59e0b", fontSize: "9px", padding: "2px 6px", borderRadius: "3px", fontWeight: 700 }}>NOMINEE</span>}
        </div>
      </div>
      <div style={{ color: "#64748b", fontSize: "10px", fontFamily: "ui-monospace, monospace" }}>{d.din}</div>
      <div style={{ color: "#94a3b8", fontSize: "11px", marginTop: "4px" }}>
        Appointed {d.appointment_date}
        {d.resignation_date && <span style={{ color: "#ef4444" }}> · Resigned {d.resignation_date}</span>}
        <span style={{ color: "#64748b" }}> · {d.other_directorships || 0} other directorships</span>
      </div>
    </div>
  );
}

function ChargeCard({ c }) {
  const isOpen = (c.status || "").toLowerCase() === "open";
  return (
    <div style={{ background: "#080c12", border: isOpen ? "1px solid #f59e0b" : "1px solid #1e2530", borderRadius: "8px", padding: "10px 12px" }}>
      <div className="flex items-center justify-between">
        <div>
          <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{c.holder}</div>
          <div style={{ color: "#64748b", fontSize: "10px", fontFamily: "ui-monospace, monospace" }}>{c.charge_id} · {c.creation_date}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 700 }}>{formatINR(c.amount)}</div>
          <span style={{ background: isOpen ? "rgba(245,158,11,0.18)" : "rgba(34,197,94,0.15)", color: isOpen ? "#f59e0b" : "#22c55e", fontSize: "9px", padding: "2px 8px", borderRadius: "3px", fontWeight: 700 }}>{c.status}</span>
        </div>
      </div>
    </div>
  );
}

export function MCA21Panel({ initialName = null, initialCin = null, compact = false }) {
  const [companyName, setCompanyName] = useState(initialName || "");
  const [cin, setCin] = useState(initialCin || "");
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [risk, setRisk] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, sfio: 0, strike_off: 0, cases_created: 0 });

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lookups`, { withCredentials: true });
      setHistory(data.lookups || []);
      setStats(data.stats || { total: 0, sfio: 0, strike_off: 0, cases_created: 0 });
    } catch (err) { console.error("[MCA21] Failed to load history:", err); }
  }, []);

  useEffect(() => { if (!compact) loadHistory(); }, [loadHistory, compact]);

  const runLookup = async () => {
    if (!companyName.trim() && !cin.trim()) { toast.error("Enter company name or CIN"); return; }
    setLooking(true); setResult(null); setRisk(null);
    try {
      const { data } = await axios.post(
        `${API}/lookup`,
        { company_name: companyName || null, cin: cin || null },
        {
          withCredentials: true,
          timeout: 90000, // 90s — MCA21 Claude responses currently ~40s cold
          headers: { "Content-Type": "application/json" },
        }
      );
      setResult(data.result);
      setRisk(data.risk);
      toast.success(`Lookup complete: ${data.result?.status || "Unknown"}`);
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      const isGateway = [502, 503, 504].includes(e?.response?.status);
      if (isTimeout || isGateway) {
        toast.error("MCA21 lookup timed out — try again. Claude AI is busy.");
      } else {
        toast.error(e.response?.data?.detail || "Lookup failed");
      }
    } finally {
      setLooking(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/save`,
        { company_name: companyName || null, cin: cin || null, result, link_to_customer_id: null },
        { withCredentials: true }
      );
      if (data.created_case) {
        toast.success(`Saved. Case ${data.created_case.case_id} auto-created.`);
      } else {
        toast.success("MCA21 lookup saved.");
      }
      setCompanyName(""); setCin(""); setResult(null); setRisk(null);
      if (!compact) loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openCharges = (result?.charges || []).filter((c) => (c.status || "").toLowerCase() === "open");

  return (
    <div data-testid="mca21-panel">
      {!compact && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
          <StatCard label="Total Lookups" value={stats.total} color="#2563eb" icon={Briefcase} />
          <StatCard label="SFIO Probes" value={stats.sfio} color="#fca5a5" icon={FlaskConical} />
          <StatCard label="Strike Off" value={stats.strike_off} color="#ef4444" icon={UserX} />
          <StatCard label="Cases Created" value={stats.cases_created} color="#f59e0b" icon={FileText} />
        </div>
      )}

      {/* Official MCA Registry — direct Sandbox source, no AI synthesis */}
      {!compact && <MCA21SourceLookup />}

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: compact ? "16px" : "24px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles style={{ width: 18, height: 18, color: "#2563eb" }} />
          <h2 style={{ fontSize: compact ? "14px" : "16px", fontWeight: 700, margin: 0, color: "#f1f5f9" }}>
            {compact ? "MCA21 Quick Lookup" : "MCA21 — Company Registry Lookup"}
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <input
            type="text" value={cin} onChange={(e) => setCin(e.target.value.toUpperCase())}
            placeholder="CIN (e.g. U72200KA2015PTC012345)" maxLength={21}
            data-testid="mca21-cin-input"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px", fontFamily: "ui-monospace, monospace" }}
          />
          <input
            type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
            placeholder="OR Company Name" data-testid="mca21-name-input"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px" }}
          />
        </div>

        <div className="flex items-center justify-between">
          <div style={{ color: "#64748b", fontSize: "11px" }}>SFIO · NCLT · Directors · Charges · Powered by Claude Sonnet 4.5</div>
          <button
            onClick={runLookup} disabled={looking} data-testid="mca21-lookup-btn"
            style={{
              background: looking ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", color: "white", padding: "10px 22px",
              fontWeight: 600, fontSize: "13px", cursor: looking ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "8px",
            }}
          >
            {looking ? (<><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Querying MCA21...</>) : (<><Search style={{ width: 14, height: 14 }} /> Lookup MCA21</>)}
          </button>
        </div>
      </div>

      {/* COI Drag-Drop Uploader — auto OCRs CIN and triggers lookup */}
      {!compact && (
        <div style={{ marginTop: 20 }}>
          <COIUploader
            onMcaResult={(payload) => {
              if (payload?.cin) setCin(payload.cin);
              if (payload?.company_name) setCompanyName(payload.company_name);
              if (payload?.result) setResult(payload.result);
              if (payload?.risk) setRisk(payload.risk);
            }}
          />
        </div>
      )}

      {result && result.found === false && (
        <NotFoundCard result={result} enableStrategy={true} />
      )}

      {result && result.found !== false && (
        <div
          data-testid="mca21-result-card"
          style={{ background: "#0d1117", border: `1px solid ${risk?.severity_level === "critical" ? "#dc2626" : (risk?.severity_level === "high" ? "#ef4444" : "#22c55e")}`, borderRadius: "12px", padding: "24px", marginTop: "20px" }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Briefcase style={{ color: "#2563eb", width: 22, height: 22 }} />
              <div>
                <div style={{ color: "#f1f5f9", fontSize: "18px", fontWeight: 700, lineHeight: 1.2 }}>{result.company_name}</div>
                <div style={{ color: "#94a3b8", fontSize: "11px", fontFamily: "ui-monospace, monospace" }}>{result.cin}</div>
              </div>
              <span style={{ background: `${STATUS_COLORS[result.status] || "#94a3b8"}22`, color: STATUS_COLORS[result.status] || "#94a3b8", border: `1px solid ${STATUS_COLORS[result.status] || "#94a3b8"}55`, fontSize: "11px", padding: "4px 10px", borderRadius: "6px", fontWeight: 700, letterSpacing: "0.4px" }}>
                {result.status}
              </span>
              {result.is_new_company && (
                <span style={{ background: "rgba(245,158,11,0.18)", color: "#f59e0b", border: "1px solid #f59e0b", fontSize: "10px", padding: "4px 10px", borderRadius: "6px", fontWeight: 700, letterSpacing: "0.4px" }} data-testid="new-company-badge">
                  NEW · {result.incorporation_date?.split("/").pop() || "2026"}
                </span>
              )}
              {result.sfio_investigation && (
                <span style={{ background: "rgba(220,38,38,0.20)", color: "#fca5a5", border: "1px solid #dc2626", fontSize: "11px", padding: "4px 10px", borderRadius: "6px", fontWeight: 700, letterSpacing: "0.4px" }}>
                  SFIO
                </span>
              )}
            </div>
            <button
              onClick={handleSave} disabled={saving} data-testid="save-mca21-btn"
              style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "6px", color: "white", padding: "8px 16px", fontWeight: 600, fontSize: "12px", cursor: saving ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "6px" }}
            >
              {saving ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <FileText style={{ width: 12, height: 12 }} />}
              {saving ? "Saving..." : "Save & Flag"}
            </button>
          </div>

          {result.new_company_note && (
            <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid #f59e0b55", borderRadius: 6, padding: "10px 14px", marginBottom: 16, color: "#f59e0b", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <Calendar size={13} /> {result.new_company_note}
            </div>
          )}

          {result.ai_assessment && (
            <div style={{ background: "rgba(37,99,235,0.06)", border: "1px solid #2563eb55", borderRadius: 6, padding: 12, marginBottom: 16 }}>
              <div style={{ color: "#3b82f6", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={11} /> AI Compliance Assessment
                {result.recommended_action && <span style={{ background: "rgba(37,99,235,0.15)", color: "#3b82f6", padding: "1px 6px", borderRadius: 3, marginLeft: "auto", fontSize: 9 }}>{result.recommended_action.replace(/_/g, " ")}</span>}
                {result.cdd_tier && <span style={{ background: "rgba(168,85,247,0.15)", color: "#a855f7", padding: "1px 6px", borderRadius: 3, fontSize: 9 }}>CDD: {result.cdd_tier}</span>}
              </div>
              <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.6 }}>{result.ai_assessment}</div>
            </div>
          )}

          {/* Risk flags */}
          {risk?.flags?.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                Regulatory Flags ({risk.flags.length}) · +{risk.risk_score_added} risk points
              </div>
              {risk.flags.map((f, idx) => {
                const c = FLAG_COLORS[f.severity] || FLAG_COLORS.medium;
                return (
                  <div key={idx} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "6px", padding: "8px 12px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <AlertTriangle style={{ width: 14, height: 14, color: c.text }} />
                    <span style={{ color: c.text, fontSize: "12px", fontWeight: 600 }}>{f.label}</span>
                    <span style={{ color: c.text, fontSize: "9px", marginLeft: "auto", textTransform: "uppercase", letterSpacing: "1px" }}>{f.severity}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* CIN Decoded (only on CIN-direct lookups) */}
          {result.cin_decoded && (
            <div style={{ background: "#080c12", border: "1px solid #2563eb55", borderRadius: 8, padding: 14, marginBottom: 16 }} data-testid="cin-decoded-panel">
              <div style={{ color: "#3b82f6", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={11} /> CIN Decoded
                {result.source === "MCA21_V3_CIN_DIRECT" && <span style={{ marginLeft: "auto", background: "rgba(34,197,94,0.18)", color: "#22c55e", border: "1px solid #22c55e55", fontSize: 9, padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>FAST-PATH</span>}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                <CINPart label="Sector"  value={result.cin_decoded.sector_code} />
                <CINPart label="NIC"     value={result.cin_decoded.nic_code} />
                <CINPart label="State"   value={result.cin_decoded.state_code} />
                <CINPart label="Year"    value={result.cin_decoded.year} />
                <CINPart label="Type"    value={result.cin_decoded.company_type} />
                <CINPart label="Serial"  value={result.cin_decoded.serial} />
              </div>
            </div>
          )}

          {/* New-company timeline */}
          {result.is_new_company && (result.days_since_incorporation > 0 || result.first_agm_due_by || result.first_bs_due_by) && (
            <div style={{ background: "rgba(245,158,11,0.06)", border: "1px solid #f59e0b55", borderRadius: 8, padding: 14, marginBottom: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }} data-testid="new-co-timeline">
              <CINPart label="Age" value={result.days_since_incorporation > 0 ? `${result.days_since_incorporation} days` : null} valueColor="#f59e0b" />
              <CINPart label="First AGM Due" value={result.first_agm_due_by} valueColor="#f59e0b" />
              <CINPart label="First BS Due"  value={result.first_bs_due_by}  valueColor="#f59e0b" />
            </div>
          )}

          {/* Company data grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "16px" }}>
            <DataPill icon={Briefcase} label="Type" value={result.company_type} />
            <DataPill icon={MapPin} label="RoC" value={result.roc_office} />
            <DataPill icon={Calendar} label="Incorporation" value={result.incorporation_date} />
            <DataPill icon={Calendar} label="Strike Off Date" value={result.strike_off_date || "—"} valueColor={result.strike_off_date ? "#ef4444" : undefined} />
            <DataPill icon={TrendingUp} label="Authorised Capital" value={formatINR(result.authorised_capital)} />
            <DataPill icon={TrendingUp} label="Paid-up Capital" value={formatINR(result.paid_up_capital)} />
            <DataPill icon={FileText} label="Filing Status" value={result.filing_status} valueColor={result.filing_status === "Defaulter" ? "#ef4444" : undefined} />
            <DataPill icon={Calendar} label="Last Annual Return" value={result.last_annual_return} />
            <DataPill icon={Calendar} label="Last Balance Sheet" value={result.last_balance_sheet} />
          </div>
          {result.registered_address && (
            <div style={{ background: "#080c12", padding: "10px 12px", borderRadius: "6px", marginBottom: "16px", fontSize: "12px", color: "#94a3b8" }}>
              <strong style={{ color: "#cbd5e1" }}>Registered Address:</strong> {result.registered_address}
            </div>
          )}

          {/* NCLT */}
          {result.nclt_proceedings && (
            <div style={{ background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", borderRadius: "6px", padding: "10px 14px", marginBottom: "16px", color: "#fca5a5", fontSize: "12px" }}>
              ⚖️ <strong>NCLT proceedings active</strong> {result.nclt_case_no && `— ${result.nclt_case_no}`}
            </div>
          )}

          {/* Directors */}
          {result.directors?.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <div className="flex items-center gap-2 mb-2">
                <Users style={{ width: 14, height: 14, color: "#64748b" }} />
                <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px" }}>
                  Directors ({result.directors.length})
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                {result.directors.map((d, idx) => <DirectorCard key={idx} d={d} />)}
              </div>
            </div>
          )}

          {/* Charges */}
          {result.charges?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Receipt style={{ width: 14, height: 14, color: "#64748b" }} />
                <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px" }}>
                  Charges ({result.charges.length}) · {openCharges.length} Open
                </div>
              </div>
              <div style={{ display: "grid", gap: "6px" }}>
                {result.charges.map((c, idx) => <ChargeCard key={idx} c={c} />)}
              </div>
            </div>
          )}

          <div style={{ marginTop: "12px", color: "#475569", fontSize: "10px", textAlign: "right" }}>
            AI: {result.ai_mode || "claude-sonnet-4.5"} · Source: {result.source}
          </div>
        </div>
      )}

      {!compact && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", marginTop: "24px" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 16px", color: "#f1f5f9" }}>Recent Lookups ({history.length})</h2>
          {history.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: "13px", textAlign: "center", padding: "32px" }}>
              No lookups yet. Try "Amrapali" ya "Gitanjali Gems" se SFIO/Strike-off pattern dekho.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "8px" }}>
              {history.map((l) => {
                const r = l.result || {};
                return (
                  <div key={l.id} style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr auto auto auto", alignItems: "center", gap: "16px" }}>
                    <div>
                      <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{r.company_name || l.company_name || "Unknown"}</div>
                      <div style={{ color: "#64748b", fontSize: "10px", fontFamily: "ui-monospace, monospace" }}>{r.cin || l.cin || "—"}</div>
                    </div>
                    {l.risk?.sfio && <span style={{ background: "rgba(220, 38, 38, 0.18)", color: "#fca5a5", borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>SFIO</span>}
                    <span style={{ background: `${STATUS_COLORS[r.status] || "#94a3b8"}22`, color: STATUS_COLORS[r.status] || "#94a3b8", borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>{r.status}</span>
                    <div style={{ color: "#64748b", fontSize: "10px" }}>{new Date(l.created_at).toLocaleDateString()}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MCA21Page() {
  return (
    <div data-testid="mca21-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-2">
        <Briefcase style={{ width: 26, height: 26, color: "#2563eb" }} />
        <h1 style={{ fontSize: "26px", fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">MCA21 Registry</h1>
      </div>
      <p style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "24px" }}>
        Ministry of Corporate Affairs lookup — SFIO probes, NCLT proceedings, disqualified directors, open charges &amp; shell-borrowing patterns.
      </p>
      <MCA21Panel />
    </div>
  );
}
